
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useNotificationSettings } from "@/hooks/useNotificationSettings";
import { useTwilioSettings } from "@/hooks/useTwilioSettings";
import { useRingCentralSettings } from "@/hooks/useRingCentralSettings";
import { useSmsProviders } from "@/hooks/useSmsProviders";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { Loader2, TestTube, MessageSquare, CheckCircle2, AlertTriangle } from "lucide-react";

// Separate components for each provider to avoid hook violations
const TwilioProviderStatus = () => {
  const { settings: twilioSettings } = useTwilioSettings();
  return <ProviderStatusLine name="Twilio" configured={!!twilioSettings?.is_active} />;
};

const RingCentralProviderStatus = () => {
  const { settings: ringcentralSettings } = useRingCentralSettings();
  return <ProviderStatusLine name="RingCentral" configured={!!ringcentralSettings?.is_active} />;
};

const ProviderStatusLine = ({ name, configured }: { name: string; configured: boolean }) => (
  <div className="flex items-center gap-2 text-sm">
    {configured ? (
      <CheckCircle2 className="h-4 w-4 shrink-0 text-status-success" />
    ) : (
      <AlertTriangle className="h-4 w-4 shrink-0 text-status-warning" />
    )}
    <span className="text-foreground">
      {name}{" "}
      <span className={configured ? "text-status-success" : "text-status-warning"}>
        {configured ? "configured" : "needs setup below"}
      </span>
    </span>
  </div>
);

const NotificationSettings = () => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();
  const { settings, loading, updateSettings } = useNotificationSettings();
  const { providers, loading: providersLoading } = useSmsProviders();

  const activeProvider = providers.find(p => p.is_active);

  const [discordWebhook, setDiscordWebhook] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [testNotificationType, setTestNotificationType] = useState('test');

  const isTestMode = settings?.is_test_mode ?? false;

  const handleTestModeToggle = async (checked: boolean) => {
    const success = await updateSettings({ is_test_mode: checked });
    if (success) {
      toast({
        title: checked ? "Test mode on" : "Live mode on",
        description: checked
          ? "Notifications go to Discord, not to customers."
          : `Real texts will be sent via ${activeProvider?.provider_name || 'your selected provider'}.`,
      });
    }
  };

  const handleDiscordWebhookSave = async () => {
    if (!discordWebhook.trim()) {
      toast({
        title: "Add a webhook URL first",
        description: "Paste the Discord webhook URL, then save.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    const success = await updateSettings({ discord_webhook_url: discordWebhook });
    if (success) {
      setDiscordWebhook("");
    }
    setIsSaving(false);
  };

  const sendTestNotification = async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        toast({
          title: "Sign-in expired",
          description: "We could not confirm your session. Reload the page and try again.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.functions.invoke('send-notification', {
        body: {
          type: testNotificationType,
          message: `This is a ${testNotificationType} notification from your order portal`,
          order_id: 'TEST-12345',
          order_no: 'TEST-123',
          customer_phone: '+18777804236',
          customer_name: 'Test Customer',
          amount: '25.99',
          store_name: "Mountain Mike's",
          offer_id: 'test-offer-id'
        }
      });

      if (error) throw error;

      toast({
        title: "Test notification sent",
        description: isTestMode
          ? "Check your Discord channel for the test message."
          : `A test text went out via ${activeProvider?.provider_name || 'your SMS provider'}.`,
        className: "bg-status-success text-status-success-foreground",
      });
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast({
        title: "Test notification failed",
        description: "We could not reach the notification service. Check your provider setup and try again.",
        variant: "destructive",
      });
    }
  };

  // Only show loading if the main hooks are loading
  const shouldShowLoading = loading || providersLoading;

  if (shouldShowLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const testSendBlockedReason = isTestMode
    ? (settings?.discord_webhook_url ? null : "Add a Discord webhook above to enable this.")
    : (activeProvider ? null : "Configure an SMS provider below to enable this.");

  // Render provider-specific status component
  const renderProviderStatus = () => {
    if (activeProvider?.provider_name === 'twilio') {
      return <TwilioProviderStatus />;
    }
    if (activeProvider?.provider_name === 'ringcentral') {
      return <RingCentralProviderStatus />;
    }
    return (
      <div className="flex items-center gap-2 text-sm">
        <AlertTriangle className="h-4 w-4 shrink-0 text-status-warning" />
        <span className="text-muted-foreground">No provider selected yet.</span>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="h-5 w-5 text-muted-foreground" />
          Sending mode
        </CardTitle>
        <CardDescription>
          Choose whether notifications go to customers or to a test channel.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Test / Live mode */}
        <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/50 p-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${isTestMode ? "bg-status-info/10 text-status-info" : "bg-status-success/10 text-status-success"}`}>
              <TestTube className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Label htmlFor="test-mode" className="text-sm font-medium text-foreground">
                  {isTestMode ? "Test mode" : "Live mode"}
                </Label>
                <Badge
                  variant="outline"
                  className={isTestMode ? "border-status-info/30 text-status-info" : "border-status-success/30 text-status-success"}
                >
                  {isTestMode ? "Testing" : "Sending"}
                </Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {isTestMode
                  ? "Messages are routed to Discord so you can preview them safely. Customers get nothing."
                  : `Real texts are sent to customers via ${activeProvider?.provider_name || 'your selected provider'}.`}
              </p>
            </div>
          </div>
          <Switch
            id="test-mode"
            checked={isTestMode}
            onCheckedChange={handleTestModeToggle}
            aria-label="Toggle test mode"
          />
        </div>

        {/* Discord Webhook — only relevant while testing */}
        <div className="space-y-3">
          <div>
            <Label htmlFor="discord-webhook" className="text-sm font-medium text-foreground">
              Discord webhook URL
            </Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Where test-mode messages are delivered. Required only while test mode is on.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="discord-webhook"
              type="url"
              placeholder="https://discord.com/api/webhooks/..."
              value={discordWebhook}
              onChange={(e) => setDiscordWebhook(e.target.value)}
              className="h-11 w-full sm:h-10 sm:flex-1"
            />
            <Button
              onClick={handleDiscordWebhookSave}
              disabled={isSaving || !discordWebhook.trim()}
              className="h-11 sm:h-10"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>

          {settings?.discord_webhook_url && (
            <p className="flex items-center gap-1.5 text-xs text-status-success">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Discord webhook saved
            </p>
          )}
        </div>

        {/* Active provider status */}
        <div className="rounded-lg border border-border p-4">
          <h4 className="mb-2 text-sm font-medium text-foreground">Sending provider</h4>
          {renderProviderStatus()}
          <p className="mt-2 text-xs text-muted-foreground">
            Set this up in the SMS provider section below.
          </p>
        </div>

        {/* Send a test */}
        <div className="border-t border-border pt-5">
          <Label className="text-sm font-medium text-foreground">Send a test notification</Label>
          <p className="mt-1 text-xs text-muted-foreground">Pick a message type, then send one to yourself.</p>
          <div className="mb-4 mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant={testNotificationType === 'test' ? 'default' : 'outline'} onClick={() => setTestNotificationType('test')}>Generic test</Button>
            <Button size="sm" variant={testNotificationType === 'order_confirmation' ? 'default' : 'outline'} onClick={() => setTestNotificationType('order_confirmation')}>Order confirmation</Button>
            <Button size="sm" variant={testNotificationType === 'order_ready' ? 'default' : 'outline'} onClick={() => setTestNotificationType('order_ready')}>Order ready</Button>
          </div>
          <Button
            onClick={sendTestNotification}
            variant="outline"
            className="h-11 w-full"
            disabled={!!testSendBlockedReason}
          >
            Send test notification
          </Button>
          {testSendBlockedReason && (
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {testSendBlockedReason}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
